import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Contact, Search, SearchX, UserPlus, X } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { clientListQuery, type ClientFilter } from '@/api/clients'
import { branchesQuery } from '@/api/queries'
import type { Branch, Client } from '@/api/types'
import { useCompanyCaller } from '@/app/company'
import { AddClientPanel } from '@/app/client-form'
import { NothingForRole } from '@/app/nothing-for-role'
import { clientAbilities, placeableBranches, visibleBranches } from '@/domain/client-abilities'
import { clientName, draftFromSearch, formatPhone, type ClientDraft } from '@/domain/clients'
import { errorText } from '@/domain/errors'
import { formatDate, t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { wellClass } from '@/ui/field'
import { Select } from '@/ui/select'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { NeutralSegmented } from '@/ui/segmented'
import { LoadingBlock, Spinner } from '@/ui/spinner'

type ListSearch = { q?: string | undefined; archived?: boolean | undefined; branch?: string | undefined }

export const Route = createFileRoute('/_app/c/$orgId/clients/')({
  validateSearch: (raw: Record<string, unknown>): ListSearch => ({
    ...(typeof raw['q'] === 'string' && raw['q'] !== '' ? { q: raw['q'] } : {}),
    ...(raw['archived'] === true || raw['archived'] === 'true' ? { archived: true } : {}),
    ...(typeof raw['branch'] === 'string' && raw['branch'] !== '' ? { branch: raw['branch'] } : {}),
  }),
  component: Clients,
})

const c = t.clients

/// «Олена Коваленко», or the phone / card when the desk typed no name.
const rowTitle = (client: Client): string =>
  clientName(client) ?? (client.phone ? formatPhone(client.phone) : client.cardNumber ? c.card(client.cardNumber) : c.unnamed)

/// The phone under the name — unless the name already IS the phone.
const rowPhone = (client: Client): string | null =>
  clientName(client) !== null && client.phone ? formatPhone(client.phone) : null

/// What a desk needs to tell people apart and to let them in, as columns
/// (X2): who, how to reach them, which club, whether the medical
/// certificate still holds (no certificate — no entry), and since when. The
/// club column only when there is more than one club to tell apart. On a
/// phone a row is the name over the phone (and club); the rest is on the
/// card. An unknown fact is an empty cell, not a column of dashes (Attio).
const clientColumns = (branchOf: ((client: Client) => ReactNode) | null): readonly DataColumn<Client>[] => [
  {
    key: 'name',
    header: c.columns.client,
    width: 'minmax(0,1.5fr)',
    cell: (client) => (
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={clientName(client) ?? '·'} className="size-7 text-[11px] max-sm:hidden" />
        <span className="truncate">{rowTitle(client)}</span>
      </span>
    ),
  },
  {
    key: 'phone',
    header: c.columns.phone,
    width: '152px',
    cell: (client) => {
      const phone = rowPhone(client)
      return phone ? <span className="tabular">{phone}</span> : null
    },
  },
  {
    key: 'email',
    header: c.columns.email,
    width: 'minmax(0,1.2fr)',
    hideBelow: 'lg',
    mobile: 'hidden',
    cell: (client) => client.email,
  },
  ...(branchOf
    ? [{ key: 'branch', header: c.columns.branch, width: 'minmax(0,0.8fr)', hideBelow: 'md', cell: branchOf } as const]
    : []),
  {
    key: 'certificate',
    header: c.columns.certificate,
    width: '128px',
    hideBelow: 'xl',
    mobile: 'hidden',
    cell: (client) =>
      client.certificate === 'expired' ? (
        <span className="text-danger">{c.certificateExpired}</span>
      ) : client.certificate === 'valid' && client.medicalCertificateUntil ? (
        <span className="tabular">{c.certificateUntil(formatDate(client.medicalCertificateUntil))}</span>
      ) : null,
  },
  {
    key: 'added',
    header: c.columns.added,
    width: '112px',
    numeric: true,
    hideBelow: 'md',
    mobile: 'hidden',
    cell: (client) => formatDate(client.createdAt),
  },
]

/// Клієнти: the desk's search. Typed text goes to the server (name, phone in
/// any shape, email, card) after a short pause, and into the address — so
/// «Назад» from a card comes back to the same search. Enter opens the only
/// match; «/» jumps to the box from anywhere on the page.
function Clients() {
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { access } = useCompanyCaller(orgId)
  const can = clientAbilities(access)
  const branches = useQuery({ ...branchesQuery(orgId), enabled: can.see })
  const [text, setText] = useState(search.q ?? '')
  const [adding, setAdding] = useState<ClientDraft | null>(null)
  const box = useRef<HTMLInputElement>(null)

  const filter: ClientFilter = {
    q: search.q ?? '',
    status: search.archived ? 'archived' : 'active',
    branchId: search.branch ?? null,
  }
  const list = useInfiniteQuery({ ...clientListQuery(orgId, filter), enabled: can.see })

  // The pause before a search: a desk types a phone in bursts.
  useEffect(() => {
    const next = text.trim()
    if (next === (search.q ?? '')) return
    const timer = window.setTimeout(() => {
      void navigate({ search: (prev) => ({ ...prev, q: next || undefined }), replace: true })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [text, search.q, navigate])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        box.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!can.see) return <NothingForRole />

  const allBranches = branches.data ?? []
  const filterBranches = visibleBranches(access, allBranches)
  const branchName = (id: string | null): string | null =>
    id === null ? null : (allBranches.find((b) => b.id === id)?.name ?? null)
  const showBranch = filterBranches.length >= 2
  const columns = clientColumns(
    showBranch
      ? (client) => branchName(client.branchId) ?? <span className="text-muted">{c.noBranch}</span>
      : null,
  )

  const rows = list.data?.pages.flatMap((page) => page.clients) ?? []
  const total = list.data?.pages[0]?.total
  const searching = filter.q !== ''
  const stale = list.isPlaceholderData || text.trim() !== filter.q

  const openClient = (clientId: string, created?: 'ok' | 'consents_failed') =>
    void navigate({
      to: '/c/$orgId/clients/$clientId',
      params: { orgId, clientId },
      search: created ? { created } : {},
    })

  const startAdding = (draft: ClientDraft) => setAdding(draft)
  const addButton = can.register ? (
    <Button
      variant="primary"
      icon={<UserPlus className="size-[18px]" strokeWidth={2.25} />}
      onClick={() => startAdding(draftFromSearch(searching ? filter.q : ''))}
    >
      {c.add}
    </Button>
  ) : undefined

  const subtitle = can.own
    ? c.subtitleOwn
    : !access.god && access.branchIds && access.branchIds.length > 0
      ? c.subtitleBranches
      : undefined

  return (
    <Page wide>
      <PageHeader
        title={c.title}
        meta={total !== undefined && !searching && filter.status === 'active' && !filter.branchId ? String(total) : undefined}
        subtitle={subtitle}
        actions={addButton}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className={`${wellClass(false)} min-w-0 flex-1 basis-[320px] [--well:var(--surface)] sm:min-h-10`}>
          <Search aria-hidden="true" className="ml-4 size-[18px] shrink-0 text-muted" />
          <input
            ref={box}
            type="search"
            autoFocus
            aria-label={c.search}
            placeholder={c.searchPlaceholder}
            title={c.searchHint}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !stale && rows.length === 1 && rows[0]) openClient(rows[0].id)
              if (e.key === 'Escape' && text !== '') setText('')
            }}
            className="h-11 min-w-0 flex-1 bg-transparent px-3 text-callout sm:h-10 text-text outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:hidden"
          />
          {list.isFetching && !list.isFetchingNextPage ? <Spinner className="mr-2 size-4 text-muted" /> : null}
          {text !== '' ? (
            <button
              type="button"
              aria-label={c.clearSearch}
              onClick={() => {
                setText('')
                box.current?.focus()
              }}
              className="mr-0.5 inline-flex size-11 shrink-0 items-center justify-center rounded-[12px] text-muted hover:text-text sm:size-9"
            >
              <X className="size-[18px]" />
            </button>
          ) : null}
        </div>
        {showBranch ? (
          <BranchFilter
            branches={filterBranches}
            value={filter.branchId}
            onChange={(branch) =>
              void navigate({ search: (prev) => ({ ...prev, branch: branch ?? undefined }), replace: true })
            }
          />
        ) : null}
        <NeutralSegmented
          label={c.status}
          value={filter.status}
          options={[
            { value: 'active', label: c.statusActive },
            { value: 'archived', label: c.statusArchived },
          ]}
          onChange={(status) =>
            void navigate({ search: (prev) => ({ ...prev, archived: status === 'archived' ? true : undefined }), replace: true })
          }
        />
      </div>

      {list.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : list.isError && rows.length === 0 ? (
        <InlineBanner
          kind="error"
          message={errorText(list.error)}
          action={{ label: t.app.retry, onClick: () => void list.refetch() }}
        />
      ) : rows.length === 0 ? (
        searching ? (
          <EmptyState
            icon={<SearchX />}
            title={c.noMatches(filter.q)}
            message={c.noMatchesMessage}
            action={
              can.register && filter.status === 'active' ? (
                <Button
                  variant="secondary"
                  icon={<UserPlus className="size-[18px]" />}
                  onClick={() => startAdding(draftFromSearch(filter.q, filter.branchId))}
                >
                  {c.addFromSearch(filter.q)}
                </Button>
              ) : undefined
            }
          />
        ) : filter.status === 'archived' ? (
          <EmptyState icon={<Contact />} title={c.emptyArchive} message={c.emptyArchiveMessage} />
        ) : can.own ? (
          <EmptyState icon={<Contact />} title={c.emptyOwn} message={c.emptyOwnMessage} />
        ) : (
          <EmptyState
            icon={<Contact />}
            title={c.empty}
            message={
              <>
                {c.emptyMessage}
                {can.register ? null : <span className="mt-2 block text-muted">{c.emptyNotYours}</span>}
              </>
            }
            action={addButton}
          />
        )
      ) : (
        <div className={stale ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <DataList
            label={c.title}
            columns={columns}
            rows={rows}
            rowKey={(client) => client.id}
            rowTarget={(client) => ({ to: '/c/$orgId/clients/$clientId', params: { orgId, clientId: client.id } })}
            rowLabel={(client) => `${c.open}: ${rowTitle(client)}`}
            footer={
              <>
                <span aria-live="polite" className="tabular">
                  {total === undefined ? null : total > rows.length ? c.shown(rows.length, total) : c.count(total)}
                </span>
                {list.hasNextPage ? (
                  <Button variant="secondary" compact busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                    {list.isFetchingNextPage
                      ? c.loadingMore
                      : total !== undefined
                        ? c.loadMore(Math.min(total - rows.length, 50))
                        : c.loadMoreUnknown}
                  </Button>
                ) : null}
              </>
            }
          />
          {list.isError ? (
            <InlineBanner
              kind="error"
              className="mt-4"
              message={errorText(list.error)}
              action={{ label: t.app.retry, onClick: () => void list.refetch() }}
            />
          ) : null}
        </div>
      )}

      {adding ? (
        <AddClientPanel
          open
          initial={adding}
          orgId={orgId}
          branches={placeableBranches(access, allBranches)}
          onClose={() => setAdding(null)}
          onCreated={(client, consentsFailed) => {
            setAdding(null)
            openClient(client.id, consentsFailed ? 'consents_failed' : 'ok')
          }}
        />
      ) : null}
    </Page>
  )
}

/// The club the list is narrowed to — a native select: a network has a
/// handful of clubs, and the browser's own list is the fastest to use.
const BranchFilter = ({
  branches,
  value,
  onChange,
}: {
  branches: readonly Branch[]
  value: string | null
  onChange: (branchId: string | null) => void
}) => (
  <Select
    variant="toolbar"
    ariaLabel={c.branchFilter}
    value={value ?? ''}
    onChange={(next) => onChange(next || null)}
    options={[{ value: '', label: c.allBranches }, ...branches.map((branch) => ({ value: branch.id, label: branch.name }))]}
  />
)
